import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DotSpinner,
  RadioGroup,
  RadioGroupItem,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@crono-one/design-system';
import Avatar from 'components/Avatar';
import { LinkedinCompanyCompleteInfo } from 'crono-fe-common/types/crono-extension/linkedin';
import { stringAvatarAccount } from 'crono-fe-common/utils';
import useCompanyInfoWithFallback from 'hooks/services/findNew/useCompanyInfoWithFallback';
import useLinkedinCompanySearch from 'hooks/services/linkedin/useLinkedinCompanySearch';
import { useState } from 'react';

export interface LinkedinCompanySearchDialogProps {
  title: string;
  /** The company name searched on LinkedIn when the dialog opens. */
  searchText: string;
  /** Gets the picked company's full information; Confirm keeps loading while it runs. */
  onPick: (company: LinkedinCompanyCompleteInfo) => void | Promise<void>;
  onClose: () => void;
}

/**
 * Searches LinkedIn for the companies matching a name and lets the user
 * confirm one, whose full information is then looked up.
 */
const LinkedinCompanySearchDialog = ({
  title,
  searchText,
  onPick,
  onClose,
}: LinkedinCompanySearchDialogProps) => {
  const {
    data: searchResult,
    isInitialLoading: isSearching,
    error: searchError,
  } = useLinkedinCompanySearch(searchText);
  const { call: getCompanyInfo } = useCompanyInfoWithFallback();

  const [selectedUrl, setSelectedUrl] = useState<string | null>(null);
  const [isPicking, setIsPicking] = useState(false);
  const [pickError, setPickError] = useState<string | null>(null);

  const companies = (searchResult?.companies ?? []).filter(
    (company) => !!company.url,
  );

  const hasNoResults =
    !isSearching && !!(searchResult || searchError) && companies.length === 0;
  const noResultsMessage =
    searchError instanceof Error
      ? searchError.message
      : 'No companies found with this name';

  const handleConfirm = async () => {
    if (!selectedUrl) {
      return;
    }

    setIsPicking(true);
    setPickError(null);

    try {
      const info = await getCompanyInfo({ url: selectedUrl });
      if (info?.company) {
        await onPick(info.company);
      } else {
        setPickError("Couldn't get this company's information, try again");
      }
    } finally {
      setIsPicking(false);
    }
  };

  // The tier goes on the portal, as in CompanySearchModal: it lifts the dialog
  // over the BlurBackgroundDiv overlays (z-index 5000) it can open from.
  return (
    <Dialog
      open
      onOpenChange={(isOpen) => {
        // The pick would still land after closing, so it can't be closed meanwhile.
        if (!isOpen && !isPicking) onClose();
      }}
    >
      <DialogContent className="w-131 h-144 gap-6" portalClassName="z-5005">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        {/* Only the companies scroll, so the error below them stays in view. */}
        {isSearching ? (
          <div className="flex-1 flex items-center justify-center">
            <DotSpinner />
          </div>
        ) : (
          <DialogBody>
            {hasNoResults && (
              <p className="text-sm text-gray-700">{noResultsMessage}</p>
            )}

            <RadioGroup
              aria-label="LinkedIn companies"
              disabled={isPicking}
              value={selectedUrl}
              onValueChange={(url) => setSelectedUrl(url as string)}
              className="gap-0"
            >
              {companies.map((company) => (
                <label
                  key={company.url}
                  className="shrink-0 min-w-0 flex items-center justify-between gap-3 p-3 cursor-pointer border-b border-neutral-300 transition-colors last:border-none hover:bg-gray-100"
                >
                  <span className="flex-1 flex items-center gap-3 overflow-hidden">
                    <Avatar
                      name={company.title}
                      src={company.logoUrl}
                      fallbackText={stringAvatarAccount}
                    />
                    <Tooltip delayDuration={800}>
                      <TooltipTrigger asChild>
                        <span className="truncate text-sm font-medium">
                          {company.title}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="z-5006">
                        {company.title}
                      </TooltipContent>
                    </Tooltip>
                  </span>
                  <RadioGroupItem value={company.url} />
                </label>
              ))}
            </RadioGroup>
          </DialogBody>
        )}

        {pickError && (
          <p role="alert" className="text-sm text-red-700">
            {pickError}
          </p>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            tone="gray"
            size="lg"
            className="min-w-40"
            disabled={isPicking}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            size="lg"
            className="min-w-40"
            loading={isPicking}
            disabled={!selectedUrl}
            onClick={handleConfirm}
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LinkedinCompanySearchDialog;
